// src/pages/SellerIntelligence/ProfitMargin/CostSetup.js
// Page 2 — where the seller enters what a unit costs them.
//
// Separated from the dashboard because entering and reading are different tasks.
// A cost carries an effective date: adding a revision is forward-looking, while
// correcting one rewrites history from its date. The two are distinct actions
// here for that reason.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertTriangle, FiArrowLeft, FiBarChart2, FiChevronDown, FiChevronRight,
  FiDownload, FiUpload,
} from 'react-icons/fi';

import styles from '../SellerIntelligence.module.css';
import local from './ProfitMargin.module.css';
import AuthContext from '../../../context/AuthContext';
import { Money, Percent, Blank, count, formatDate, todayIso } from './format';
import {
  getCosts, saveCost, deleteBatch, saveSettings, importCosts, downloadCosts,
} from './services/profitApi';

const FILTERS = [
  { id: 'all', label: 'All SKUs' },
  { id: 'missing', label: 'Cost not set' },
  { id: 'set', label: 'Cost set' },
];

const CostSetup = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingSku, setSavingSku] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('missing');
  const [search, setSearch] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [settings, setSettings] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [recalculated, setRecalculated] = useState(null);
  const [showThresholds, setShowThresholds] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await getCosts(token);
      setData(payload);
      setSettings(payload.settings);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const term = search.trim().toLowerCase();
    return all.filter((row) => {
      if (term && !row.sku.toLowerCase().includes(term)) return false;
      if (filter === 'missing') return row.cost === null || row.cost === undefined;
      if (filter === 'set') return row.cost !== null && row.cost !== undefined;
      return true;
    });
  }, [data, search, filter]);

  const commit = async (sku) => {
    const draft = drafts[sku];
    if (draft === undefined || draft === '') return;
    setSavingSku(sku);
    setError(null);
    try {
      const saved = await saveCost(token, sku, {
        cost: Number(draft),
        effective_from: effectiveFrom,
        source: 'actual',
      });
      // A SKU's first cost applies to everything it sold before, so past
      // profit has changed and the seller should know.
      if (saved?.history_recalculated) setRecalculated([sku]);
      setDrafts((current) => {
        const next = { ...current };
        delete next[sku];
        return next;
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSavingSku(null);
    }
  };

  const removeBatch = async (batchId) => {
    try {
      await deleteBatch(token, batchId);
      await load();
    } catch (err) {
      setError(err);
    }
  };

  const persistSettings = async (changes) => {
    setSettings((current) => ({ ...current, ...changes }));
    try {
      setSettings(await saveSettings(token, changes));
    } catch (err) {
      setError(err);
    }
  };

  // A blank threshold returns to its shipped default.
  const persistThreshold = (key, raw) => persistSettings({
    thresholds: { [key]: raw === '' ? null : Number(raw) },
  });

  const onImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportResult(null);
    setImporting(true);
    setError(null);
    try {
      const result = await importCosts(token, file);
      setImportResult(result);
      if (result?.history_recalculated) setRecalculated(result.history_recalculated);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const coverage = data?.coverage;
  const coverageLow = (coverage?.revenue_pct ?? 0) < 80;

  const header = (
    <div className={styles.header}>
      <button className={styles.backButton}
        onClick={() => navigate('/dashboard/seller-intelligence/profit')}>
        <FiArrowLeft /> Back to profit
      </button>
      <div className={styles.titleSection}>
        <div className={styles.iconTitle}><h1>Cost Setup</h1></div>
        <p>Enter what each unit costs you once — it applies to every period you select</p>
      </div>
    </div>
  );

  if (error?.notConnected) {
    return (
      <div className={styles.featureContainer}>{header}
        <div className={styles.content}>
          <div className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>{error.message}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.featureContainer}>
      {header}
      <div className={styles.content}>

        {error && !error.notConnected && (
          <div className={`${local.banner} ${local.bannerError}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>{error.message}</div>
          </div>
        )}

        {recalculated && (
          <div className={`${local.banner} ${local.bannerInfo}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              {Array.isArray(recalculated)
                ? `${recalculated[0]} had no cost before, so this cost now applies to everything it sold earlier.`
                : `${recalculated} SKUs had no cost before, so their new costs now apply to everything they sold earlier.`}
              {' '}Profit for past periods has been recalculated.{' '}
              <button type="button" className={local.linkBtn}
                onClick={() => setRecalculated(null)}>Dismiss</button>
            </div>
          </div>
        )}

        {data?.notes?.map((note) => (
          <div key={note} className={`${local.banner} ${local.bannerWarn}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>{note}</div>
          </div>
        ))}

        <div className={local.card}>
          <div className={local.cardHead}>
            <h2 className={local.cardTitle}>Coverage</h2>
            <button className={local.ghost}
              onClick={() => navigate('/dashboard/seller-intelligence/profit')}>
              <FiBarChart2 /> View profit
            </button>
          </div>
          <div className={`${local.coverageBar} ${coverageLow ? local.coverageLow : ''}`}>
            <div className={local.coverageFill}
              style={{ width: `${Math.min(coverage?.revenue_pct ?? 0, 100)}%` }} />
          </div>
          <p className={local.note}>
            <strong><Percent value={coverage?.revenue_pct} /></strong> of revenue has a cost
            behind it. {coverage?.skus_total - (coverage?.skus_with_cost ?? 0)} SKUs still need
            one, leaving{' '}
            <Money value={(coverage?.total_revenue ?? 0) - (coverage?.covered_revenue ?? 0)} />{' '}
            of revenue unmeasurable.
          </p>
          <p className={local.note}>
            Fastest way in: upload a spreadsheet with a <strong>SKU</strong> column and a{' '}
            <strong>COGS</strong> column. Anything else in the file is ignored, and your
            first upload applies to periods you have already traded.
          </p>
        </div>

        <div className={local.card}>
          <h2 className={local.cardTitle}>Defaults</h2>
          <div className={local.settingsGrid}>
            <label className={local.field}>
              <span>Minimum acceptable margin (%)</span>
              <input type="number" min="0" max="100" step="0.5"
                placeholder="Not set"
                value={settings?.margin_floor_set ? (settings?.min_acceptable_margin_pct ?? '') : ''}
                onChange={(e) => setSettings({
                  ...settings, margin_floor_set: true,
                  min_acceptable_margin_pct: e.target.value === '' ? '' : Number(e.target.value),
                })}
                onBlur={(e) => e.target.value !== '' && persistSettings({
                  min_acceptable_margin_pct: Number(e.target.value),
                })} />
              <span className={local.muted}>
                {settings?.margin_floor_set
                  ? 'Products below this are flagged, not only those losing money.'
                  : 'Not set yet, so no product is flagged for falling below a floor.'}
              </span>
            </label>
            <label className={local.field}>
              <span>Estimated cost (% of price)</span>
              <input type="number" min="0" max="100" step="1" placeholder="Off"
                value={settings?.estimated_cost_pct_of_price ?? ''}
                onChange={(e) => setSettings({
                  ...settings, estimated_cost_pct_of_price: e.target.value,
                })}
                onBlur={(e) => persistSettings({
                  estimated_cost_pct_of_price: e.target.value === '' ? null : Number(e.target.value),
                })} />
              <span className={local.muted}>
                Gives SKUs with no cost a figure from day one. Everything built on it is
                marked est.
              </span>
            </label>
            <label className={local.field}>
              <span>Default GST rate (%)</span>
              <input type="number" min="0" max="100" step="0.5"
                value={settings?.default_gst_rate ?? ''}
                onChange={(e) => setSettings({
                  ...settings, default_gst_rate: Number(e.target.value),
                })}
                onBlur={(e) => persistSettings({ default_gst_rate: Number(e.target.value) })} />
              <span className={local.muted}>Per-SKU override available below.</span>
            </label>
            <label className={local.field}>
              <span>New costs effective from</span>
              <input type="date" value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)} />
              <span className={local.muted}>
                Applies to costs you enter now. Earlier periods keep their own.
              </span>
            </label>
          </div>

          <button type="button" className={local.linkBtn} style={{ marginTop: 10 }}
            onClick={() => setShowThresholds((v) => !v)}>
            {showThresholds ? 'Hide' : 'Show'} finding thresholds
          </button>
          {showThresholds && (
            <div className={local.settingsGrid} style={{ marginTop: 10 }}>
              {Object.entries(data?.threshold_defaults ?? {}).map(([key, fallback]) => (
                <label key={key} className={local.field}>
                  <span>{key.replaceAll('_', ' ')}</span>
                  <input type="number" min="0" step="any" placeholder={String(fallback)}
                    defaultValue={settings?.thresholds?.[key] ?? ''}
                    onBlur={(e) => persistThreshold(key, e.target.value)} />
                  <span className={local.muted}>Default {fallback}. Leave blank to use it.</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className={local.card}>
          <div className={local.cardHead}>
            <h2 className={local.cardTitle}>
              Costs — {rows.length} of {data?.rows?.length ?? 0} SKUs
            </h2>
            <div className={local.bar} style={{ margin: 0 }}>
              <input className={local.costInput} style={{ width: 170, textAlign: 'left' }}
                placeholder="Search SKU" value={search}
                onChange={(e) => setSearch(e.target.value)} />
              <select value={filter} onChange={(e) => setFilter(e.target.value)}
                style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #e3e6ea' }}>
                {FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
              <label className={`${local.ghost} ${importing ? local.busy : ''}`}
                style={{ margin: 0 }}>
                <FiUpload /> {importing ? 'Uploading…' : 'Upload COGS'}
                <input type="file" accept=".csv,.xlsx,.xls,text/csv" onChange={onImport}
                  disabled={importing} style={{ display: 'none' }} />
              </label>
              <button className={local.ghost} onClick={() => downloadCosts(token)}>
                <FiDownload /> Export
              </button>
            </div>
          </div>

          {importResult && (
            <div className={`${local.banner} ${importResult.failed ? local.bannerWarn : local.bannerInfo}`}>
              <FiAlertTriangle className={local.bannerIcon} />
              <div>
                Imported {importResult.saved} rows
                {importResult.failed > 0 && `, ${importResult.failed} failed`}.
                {importResult.errors?.slice(0, 3).map((e) => (
                  <div key={`${e.row}-${e.error}`} className={local.muted}>
                    Row {e.row}{e.sku ? ` (${e.sku})` : ''}: {e.error}
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? <div className={local.empty}>Loading SKUs…</div>
            : rows.length === 0 ? <div className={local.empty}>No SKUs match.</div> : (
              <div className={local.tableScroll}>
                <table className={local.table}>
                  <thead>
                    <tr>
                      <th style={{ width: 28 }} />
                      <th>SKU</th>
                      <th className={local.numeric}>Units sold</th>
                      <th className={local.numeric}>Net revenue</th>
                      <th className={local.numeric}>Avg price</th>
                      <th className={local.numeric}>Cost / unit</th>
                      <th>Effective from</th>
                      <th className={local.numeric}>Revisions</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const draft = drafts[row.sku];
                      const dirty = draft !== undefined && draft !== '';
                      return (
                        <React.Fragment key={row.sku}>
                          <tr>
                            <td>
                              <button className={local.expandBtn}
                                onClick={() => setExpanded(expanded === row.sku ? null : row.sku)}
                                aria-label={`Cost history for ${row.sku}`}>
                                {expanded === row.sku ? <FiChevronDown /> : <FiChevronRight />}
                              </button>
                            </td>
                            <td>{row.sku}</td>
                            <td className={local.numeric}>{count(row.units)}</td>
                            <td className={local.numeric}><Money value={row.net_revenue} signed /></td>
                            <td className={local.numeric}>
                              <Money value={row.avg_realised_price} paise />
                            </td>
                            <td className={local.numeric}>
                              <input
                                className={`${local.costInput} ${dirty ? local.costInputDirty : ''}`}
                                type="number" min="0" step="0.01"
                                placeholder={row.cost ?? '—'}
                                value={draft ?? (row.cost ?? '')}
                                onChange={(e) => setDrafts({ ...drafts, [row.sku]: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Enter') commit(row.sku); }} />
                            </td>
                            <td>{row.effective_from ? formatDate(row.effective_from) : <Blank />}</td>
                            <td className={local.numeric}>{row.batch_count || <Blank />}</td>
                            <td>
                              {dirty && (
                                <button className={local.primary} style={{ padding: '5px 12px' }}
                                  disabled={savingSku === row.sku}
                                  onClick={() => commit(row.sku)}>
                                  {savingSku === row.sku ? 'Saving…' : 'Save'}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded === row.sku && (
                            <tr className={local.expandRow}>
                              <td colSpan={9}>
                                <div className={local.expandInner}>
                                  <div className={local.cardTitle} style={{ marginBottom: 8 }}>
                                    Cost history
                                  </div>
                                  {row.batches?.length ? (
                                    <table className={local.subTable}>
                                      <thead>
                                        <tr>
                                          <th>Effective from</th>
                                          <th className={local.numeric}>Cost</th>
                                          <th className={local.numeric}>Freight</th>
                                          <th className={local.numeric}>Prep</th>
                                          <th>Source</th>
                                          <th />
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {row.batches.map((batch) => (
                                          <tr key={batch.batch_id}>
                                            <td>{formatDate(batch.effective_from)}</td>
                                            <td className={local.numeric}>
                                              <Money value={batch.cost} paise />
                                            </td>
                                            <td className={local.numeric}>
                                              <Money value={batch.inbound_freight} paise />
                                            </td>
                                            <td className={local.numeric}>
                                              <Money value={batch.prep_cost} paise />
                                            </td>
                                            <td>{batch.source || 'actual'}</td>
                                            <td>
                                              <button className={local.ghost}
                                                style={{ padding: '3px 10px', fontSize: 12 }}
                                                onClick={() => removeBatch(batch.batch_id)}>
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <span className={local.muted}>
                                      No cost entered yet. Profit for this SKU is blank rather
                                      than calculated against a cost of zero.
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default CostSetup;
